import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider, KeyboardDateTimePicker } from "@material-ui/pickers";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import NativeSelect from "@meetyoo/components/src/components/Selects/NativeSelect";

import LocalizedUtils, { moment } from "../../../utils/LocalizedUtils";

import { setFilter, clearFilter, reCheckErrors } from "../../../actions/billing";

const useStyles = makeStyles(theme => ({
  filterContainer: {
    display: "flex",
    flexWrap: "wrap",
    width: "100%",
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  buttonContainer: {
    marginTop: theme.spacing(1),
    width: "100%",
    display: "inline-flex",
  },
  spaceButtons: {
    flex: "1",
    width: "10px",
  },
  left: {
    alignContent: theme.left,
    marginLeft: "8px",
  },
  lineExpander: {
    flexGrow: 1,
  },
  lineBreak: {
    width: "100%",
  },
}));

const customStyle = () => ({
  formControl: { width: "500px" },
});

const objectBillingPoint = {
  EMPTY: "",
  ALLES: "Alle Posten",
  KM: "Kalendermonat",
  KM_VORAUS: "Kalendermonat im Voraus",
  PERIODE: "Abrechnungsperiode",
  QM: "Kalenderquartal",
  VJ: "Vertragsjahr",
  VJ_VORAUS: "Vertragsjahr im Voraus",
  VM: "Vertragsmonat",
  VM_VORAUS: "Vertragsmonat im Voraus",
  VQ_VORAUS: "Vertragsquartal im Voraus",
};

const objectBillingTime = {
  EMPTY: "",
  "1+16 KM": "1. und 16. im Kalendermonat (regelmäßig)",
  "15KM": "15. Tag im Kalendermonat",
  "16+LT KM": "16. und letzter Tag im Kalendermonat (regelmäßig)",
  "ERSTER KM": "erster Tag im Kalendermonat (regelmäßig)",
  "ERSTER VM": "erster Tag im Vertragsmonat (regelmäßig)",
  "LETZTER KM": "letzter Tag im Kalendermonat (regelmäßig)",
  "LETZTER VM": "letzter Tag im Vertragsmonat (regelmäßig)",
  MANUELL: "keine automatische Abrechnung",
  SOFORT: "sofortige Berechnung",
  VJ: "Vertragsjahr",
  VM: "Vertragsmonat",
  VQ: "Vertragsquartal",
  "VT-BEGINN": "Vertragsbeginn (einmalig)",
  "VT-ENDE": "Vertragsende (einmalig)",
};

const filterObjectBillingPoint = selectedValue => {
  const value = Object.keys(objectBillingPoint).filter(key => objectBillingPoint[key] === selectedValue)[0];
  return value !== "EMPTY" ? value : "";
};

const filterObjectBillingTime = selectedValue => {
  const value = Object.keys(objectBillingTime).filter(key => objectBillingTime[key] === selectedValue)[0];
  return value !== "EMPTY" ? value : "";
};

export const BillingLedgerEntryFilterComponent = ({
  getLedgerEntriesHandler,
  setResetPage,
  setMadeRequest,
  requestBillingInProgress,
  translate,
  filter,
  setFilterFn,
  clearFilterFn,
  reCheckErrorsFn,
}) => {
  const handleSelectChangeBillingPoint = name => event => {
    setFilterFn({ ...filter, [name]: filterObjectBillingPoint(event.target.value) });
  };

  const handleSelectChangeBillingTime = name => event => {
    setFilterFn({ ...filter, [name]: filterObjectBillingTime(event.target.value) });
  };

  const handleTextChange = name => event => {
    setFilterFn({ ...filter, [name]: event.target.value });
  };

  const handleDateChange = (name, date) => {
    setFilterFn({ ...filter, [name]: moment.utc(date) });
  };

  const classes = useStyles();
  return (
    <div className={classes.filterContainer}>
      <Typography variant="subtitle1" id="filterTitle">
        Filter
      </Typography>
      <div className={classes.lineBreak} />
      <TextField
        id="ledger-entry-conference-id"
        label={translate.conferenceId}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.confId}
        onChange={handleTextChange("confId")}
      />
      <TextField
        id="ledger-entry-stat"
        label={translate.status}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.status}
        onChange={handleTextChange("status")}
      />
      <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
        <KeyboardDateTimePicker
          id="ledger-entry-start-time"
          label={translate.confStartTime}
          className={clsx(classes.textField, classes.lineExpander)}
          ampm={false}
          format="DD.MM.YYYY HH:mm:ss"
          value={filter.confStart}
          onChange={date => handleDateChange("confStart", date)}
        />
        <KeyboardDateTimePicker
          id="ledger-entry-end-time"
          label={translate.confEndTime}
          className={clsx(classes.textField, classes.lineExpander)}
          ampm={false}
          format="DD.MM.YYYY HH:mm:ss"
          value={filter.confEnd}
          onChange={date => handleDateChange("confEnd", date)}
        />
      </MuiPickersUtilsProvider>
      <TextField
        id="ledger-entry-error"
        label={translate.errorCodeLedgerEntry}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.error}
        onChange={handleTextChange("error")}
      />
      <TextField
        id="ledger-entry-contract"
        label={translate.contract}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.contract}
        onChange={handleTextChange("contract")}
      />
      <TextField
        id="ledger-entry-product-number"
        label={translate.phoneNumber}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.productno}
        onChange={handleTextChange("productno")}
      />
      <TextField
        id="ledger-entry-name"
        label={translate.name}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.name}
        onChange={handleTextChange("name")}
      />
      <TextField
        id="ledger-entry-request"
        label={translate.requestFrom}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.requestFrom}
        onChange={handleTextChange("requestFrom")}
      />
      <TextField
        id="ledger-entry-participant"
        label={translate.participantName}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.participantName}
        onChange={handleTextChange("participantName")}
      />
      <TextField
        id="ledger-entry-b-phone"
        label={translate.bPhoneNumber}
        className={clsx(classes.textField, classes.lineExpander)}
        value={filter.bPhoneNumber}
        onChange={handleTextChange("bPhoneNumber")}
      />
      <div className={classes.lineBreak} />
      <NativeSelect
        customStyle={customStyle}
        id="ledger-entry-billingTime"
        value={translate.billingTime}
        currentSelect={objectBillingTime[filter.billingTime !== "" ? filter.billingTime : "EMPTY"]}
        selectValues={Object.keys(objectBillingTime).map(key => objectBillingTime[key])}
        onChange={handleSelectChangeBillingTime("billingTime")}
      />
      <NativeSelect
        customStyle={customStyle}
        id="ledger-entry-billingPoint"
        value={translate.billingPoint}
        currentSelect={objectBillingPoint[filter.billingPoint !== "" ? filter.billingPoint : "EMPTY"]}
        selectValues={Object.keys(objectBillingPoint).map(key => objectBillingPoint[key])}
        onChange={handleSelectChangeBillingPoint("billingPoint")}
      />
      <Divider />
      <div className={classes.buttonContainer}>
        <div className={(classes.spaceButtons, classes.left)} />
        <Button variant="contained" size="small" color="primary" onClick={() => reCheckErrorsFn()}>
          Check errors
        </Button>
        <div className={classes.spaceButtons} />
        <Button size="small" onClick={() => clearFilterFn()}>
          {translate.clearButtonFilter}
        </Button>
        <Button
          disabled={requestBillingInProgress}
          variant="contained"
          size="small"
          color="primary"
          onClick={() => {
            setResetPage(true);
            setFilterFn({ ...filter, page: 0 });
            setMadeRequest(true);
            getLedgerEntriesHandler({ ...filter, page: 0 });
          }}
        >
          {translate.searchButtonFilter}
        </Button>
      </div>
    </div>
  );
};

BillingLedgerEntryFilterComponent.propTypes = {
  getLedgerEntriesHandler: PropTypes.func.isRequired,
  filter: PropTypes.object.isRequired,
  translate: PropTypes.object.isRequired,
  setFilterFn: PropTypes.func.isRequired,
  clearFilterFn: PropTypes.func.isRequired,
  setMadeRequest: PropTypes.func.isRequired,
  setResetPage: PropTypes.func.isRequired,
  reCheckErrorsFn: PropTypes.func.isRequired,
  requestBillingInProgress: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  filter: state.billing.filter,
  translate: state.ui.translate,
});

const mapDispatchToProps = dispatch => ({
  setFilterFn: filter => {
    dispatch(setFilter(filter));
  },
  clearFilterFn: () => dispatch(clearFilter()),
  reCheckErrorsFn: () => dispatch(reCheckErrors()),
});

const BillingLedgerEntryFilter = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingLedgerEntryFilterComponent);

export default BillingLedgerEntryFilter;
